import { c as _c } from "react-compiler-runtime";
import { useEffect, useRef, useState } from 'react';
export const getCellSelector = (rowIndex, columnIndex) => `[data-column-index="${columnIndex}"][data-fdt-row-index="${rowIndex}"]`;

/**
 * Resolves the live DOM element for a virtualized table cell identified by
 * (rowIndex, columnIndex) and keeps the reference valid as the virtualizer
 * mounts and unmounts the node.
 *
 * - Backoff retry handles the case where the cell is not yet in the DOM on
 *   the first tick (common under virtualization).
 * - A rAF-coalesced scroll + MutationObserver listener re-queries whenever
 *   the scroll container's subtree changes. Scrolling a cell out of the
 *   render window unmounts its <td> (re-query → null) and scrolling back
 *   mounts a fresh node (re-query → new element), so the caller always holds
 *   the current live node.
 */
export const useVirtualizedCellAnchor = t0 => {
  const $ = _c(21);
  const {
    rowIndex,
    columnIndex,
    scrollContainerElementRef,
    widths,
    maxWaitMs: t1,
    onTimeout
  } = t0;
  const maxWaitMs = t1 === undefined ? 2000 : t1;
  const [anchorElement, setAnchorElement] = useState(null);
  const onTimeoutRef = useRef(onTimeout);
  let t2;
  let t3;
  if ($[0] !== onTimeout) {
    t2 = () => {
      onTimeoutRef.current = onTimeout;
    };
    t3 = [onTimeout];
    $[0] = onTimeout;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== columnIndex || $[4] !== maxWaitMs || $[5] !== rowIndex || $[6] !== scrollContainerElementRef) {
    t4 = () => {
      const startedAt = Date.now();
      let nextDelayMs = 0;
      let timeoutId;
      const attempt = () => {
        const scrollContainer = scrollContainerElementRef.current;
        if (rowIndex === null || columnIndex === null || !scrollContainer) {
          setAnchorElement(null);
          return;
        }
        const found = scrollContainer.querySelector(getCellSelector(rowIndex, columnIndex));
        if (found) {
          setAnchorElement(found);
          return;
        }
        if (Date.now() - startedAt >= maxWaitMs) {
          var _onTimeoutRef$current;
          (_onTimeoutRef$current = onTimeoutRef.current) === null || _onTimeoutRef$current === void 0 || _onTimeoutRef$current.call(onTimeoutRef);
          setAnchorElement(null);
          return;
        }
        nextDelayMs = nextDelayMs === 0 ? 50 : Math.min(nextDelayMs * 2, 500);
        timeoutId = setTimeout(attempt, nextDelayMs);
      };
      timeoutId = setTimeout(attempt, 0);
      return () => clearTimeout(timeoutId);
    };
    $[3] = columnIndex;
    $[4] = maxWaitMs;
    $[5] = rowIndex;
    $[6] = scrollContainerElementRef;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== columnIndex || $[9] !== maxWaitMs || $[10] !== rowIndex || $[11] !== scrollContainerElementRef || $[12] !== widths) {
    t5 = [rowIndex, columnIndex, widths, scrollContainerElementRef, maxWaitMs];
    $[8] = columnIndex;
    $[9] = maxWaitMs;
    $[10] = rowIndex;
    $[11] = scrollContainerElementRef;
    $[12] = widths;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[14] !== columnIndex || $[15] !== rowIndex || $[16] !== scrollContainerElementRef) {
    t6 = () => {
      const scrollContainer_0 = scrollContainerElementRef.current;
      if (rowIndex === null || columnIndex === null || !scrollContainer_0) {
        return;
      }
      let rafId = null;
      const reresolve = () => {
        if (rafId !== null) {
          return;
        }
        rafId = requestAnimationFrame(() => {
          rafId = null;
          const found_0 = scrollContainer_0.querySelector(getCellSelector(rowIndex, columnIndex));
          setAnchorElement(found_0 !== null && found_0 !== void 0 ? found_0 : null);
        });
      };
      scrollContainer_0.addEventListener("scroll", reresolve, {
        passive: true
      });
      const observer = new MutationObserver(reresolve);
      observer.observe(scrollContainer_0, {
        childList: true,
        subtree: true
      });
      return () => {
        scrollContainer_0.removeEventListener("scroll", reresolve);
        observer.disconnect();
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    };
    t7 = [rowIndex, columnIndex, scrollContainerElementRef];
    $[14] = columnIndex;
    $[15] = rowIndex;
    $[16] = scrollContainerElementRef;
    $[17] = t6;
    $[18] = t7;
  } else {
    t6 = $[17];
    t7 = $[18];
  }
  useEffect(t6, t7);
  let t8;
  if ($[19] !== anchorElement) {
    t8 = {
      anchorElement
    };
    $[19] = anchorElement;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};